import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { Plus, Search, Users, DollarSign, Store, Ban, CheckCircle, Eye, TrendingUp } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAffiliates() {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", password: "" });

  useEffect(() => {
    fetchAffiliates();
  }, []);

  const fetchAffiliates = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/affiliates`, {
        credentials: "include",
        headers: getAuthHeaders(),
      });
      if (response.ok) {
        setAffiliates(await response.json());
      }
    } catch (error) {
      toast.error("Failed to load affiliates");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAffiliate = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_URL}/api/admin/affiliates`, {
        method: "POST",
        credentials: "include",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success("Affiliate created!");
        setDialogOpen(false);
        setFormData({ name: "", email: "", password: "" });
        fetchAffiliates();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create affiliate");
      }
    } catch (error) {
      toast.error("Failed to create affiliate");
    }
  };

  const handleStatusChange = async (affiliateId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/affiliates/${affiliateId}`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        toast.success(`Affiliate ${newStatus}`);
        fetchAffiliates();
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const filteredAffiliates = affiliates.filter(
    (a) =>
      a.name?.toLowerCase().includes(search.toLowerCase()) ||
      a.email?.toLowerCase().includes(search.toLowerCase()) ||
      a.public_code?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout title="Affiliates" subtitle="Manage affiliate partners">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Affiliates" subtitle="Manage affiliate partners">
      <div data-testid="admin-affiliates" className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              placeholder="Search affiliates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="affiliate-search"
              className="pl-10 bg-card border-border text-foreground"
            />
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-affiliate-btn" className="glow-primary">
                <Plus className="w-4 h-4 mr-2" /> Add Affiliate
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="text-foreground">Create Affiliate</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateAffiliate} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                    required
                    className="bg-background border-border text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    required
                    className="bg-background border-border text-foreground"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground">Password</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Min. 6 characters"
                    required
                    minLength={6}
                    className="bg-background border-border text-foreground"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 glow-primary">
                    Create Affiliate
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Users className="w-4 h-4" />
              <span className="text-sm">Total</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{affiliates.length}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <CheckCircle className="w-4 h-4" />
              <span className="text-sm">Active</span>
            </div>
            <p className="text-2xl font-bold text-emerald-400">
              {affiliates.filter((a) => a.status === "active").length}
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <Store className="w-4 h-4" />
              <span className="text-sm">Merchants</span>
            </div>
            <p className="text-2xl font-bold text-blue-400">
              {affiliates.reduce((sum, a) => sum + (a.merchants_count || 0), 0)}
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <DollarSign className="w-4 h-4" />
              <span className="text-sm">Total Paid</span>
            </div>
            <p className="text-2xl font-bold text-amber-400">
              {formatCurrency(affiliates.reduce((sum, a) => sum + (a.total_earnings || 0), 0))}
            </p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Affiliate</th>
                  <th>Code</th>
                  <th>Status</th>
                  <th>Merchants</th>
                  <th>Earnings</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAffiliates.length > 0 ? (
                  filteredAffiliates.map((affiliate) => (
                    <tr key={affiliate.id}>
                      <td>
                        <div>
                          <p className="font-medium text-foreground">{affiliate.name}</p>
                          <p className="text-xs text-slate-500">{affiliate.email}</p>
                        </div>
                      </td>
                      <td>
                        <span className="px-2 py-1 rounded text-xs font-mono bg-muted text-muted-foreground">
                          {affiliate.public_code}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            affiliate.status === "active"
                              ? "status-active"
                              : "status-suspended"
                          }`}
                        >
                          {affiliate.status}
                        </span>
                      </td>
                      <td className="text-muted-foreground">{affiliate.merchants_count || 0}</td>
                      <td className="text-emerald-400 font-medium">
                        {formatCurrency(affiliate.total_earnings)}
                      </td>
                      <td className="text-muted-foreground">{formatDate(affiliate.created_at)}</td>
                      <td>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/admin/affiliates/${affiliate.id}`)}
                            data-testid={`view-affiliate-${affiliate.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                          <Select
                            value={affiliate.status}
                            onValueChange={(value) => handleStatusChange(affiliate.id, value)}
                          >
                            <SelectTrigger className="w-[100px] bg-transparent border-border text-foreground h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="suspended">Suspend</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center text-slate-500 py-8">
                      {search ? "No affiliates match your search" : "No affiliates yet"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
